// @ts-check

/**
 * Scroll-driven helix canvas: extracts frames from a video and draws to canvas
 * based on scroll progress. Use with [data-helix-section] or initHelixCanvas().
 */

(function() {
    'use strict';

    /**
     * Initializes a scroll-driven helix canvas.
     *
     * @param {Element} section - Element used for scroll progress (getBoundingClientRect).
     */
    function initHelixCanvas(section) {
        // How much of the section's scroll the sequence is spread across.
        // Larger = the same motion is stretched over more scroll, so it
        // advances more gently (>1 means it never fully completes before the
        // section leaves, which is fine — the tail rotation isn't needed).
        const PLAY_FRACTION = 1.2;
        const START_FRACTION = 0; // 0 = start of video, 0.2 = 20% in
        const END_FRACTION = 1.0; // 1 = end of video

        // --------------------------------------------------------------------
        // Browser / viewport detection for static fallback
        // --------------------------------------------------------------------
        // Scroll-frame extraction from the helix video fails in Firefox.
        // On mobile / narrow viewports we always prefer the static image
        // (helix-mobile-2.jpg via <picture>) — no canvas motion.
        // Desktop Firefox uses fs-hebbia-spine-full.png (hero__helix-static--desktop).
        const ua = navigator.userAgent || '';
        const uaLower = ua.toLowerCase();
        const isFirefox = uaLower.indexOf('firefox') !== -1 || ua.indexOf('FxiOS') !== -1;
        const isMobileLike = /Mobile|Android|iPhone|iPad|iPod/.test(ua);
        const isNarrowViewport = typeof window.matchMedia === 'function' &&
            window.matchMedia('(max-width: 56.24em)').matches;
        const useFallbackMedia = isFirefox || isMobileLike || isNarrowViewport;

        if (useFallbackMedia) {
            if ('classList' in section) {
                section.classList.add('hero--helix-fallback');
            }
            return;
        }

        /** @type {ImageBitmap[]} */
        const frames = [];
        let targetProgress = 0;
        let timeout = 0;
        let extractionDone = false;

        /** @type {HTMLVideoElement | null} */
        const video = /** @type {HTMLVideoElement | null} */ (section.querySelector('.hero__helix'));
        const wrapper = section.querySelector('.hero__canvas-wrapper');
        /** @type {HTMLCanvasElement | null} */
        const canvas = wrapper ? /** @type {HTMLCanvasElement | null} */ (wrapper.querySelector('canvas')) : null;

        /**
         * Lazily attach video sources from data attributes so unsupported
         * browsers never trigger network requests for the helix video.
         *
         * @param {HTMLVideoElement} videoEl
         */
        function hydrateVideoSources(videoEl) {
            if (!videoEl) return;
            // Avoid duplicating sources if called more than once.
            if (videoEl.querySelector('source')) return;

            const srcMp4 = videoEl.getAttribute('data-src-mp4');
            const srcWebm = videoEl.getAttribute('data-src-webm');

            if (srcMp4) {
                const sourceMp4 = document.createElement('source');
                sourceMp4.src = srcMp4;
                sourceMp4.type = 'video/mp4';
                videoEl.appendChild(sourceMp4);
            }

            if (srcWebm) {
                const sourceWebm = document.createElement('source');
                sourceWebm.src = srcWebm;
                sourceWebm.type = 'video/webm';
                videoEl.appendChild(sourceWebm);
            }

            if (srcMp4 || srcWebm) {
                videoEl.load();
            }
        }

        /**
         * Draws a frame to the canvas based on scroll progress.
         *
         * @param {number} progress
         */
        function drawFrame(progress) {
            if (!canvas || frames.length === 0) return;
            const ctx = canvas.getContext('2d');
            if (!ctx) return;
            const maxIdx = frames.length - 1;
            const idx = Math.round(progress * maxIdx);
            const clamped = Math.max(0, Math.min(maxIdx, idx));
            const frame = frames[clamped];
            if (!frame) return;
            if (canvas.width !== frame.width || canvas.height !== frame.height) {
                canvas.width = frame.width;
                canvas.height = frame.height;
            }
            ctx.drawImage(frame, 0, 0);
        }

        function extractFrames() {
            if (!video || extractionDone) return;
            extractionDone = true;

            /** @type {HTMLVideoElement} */
            const videoEl = video;

            // Only now, on supported environments, hydrate the sources so the
            // video never loads on unsupported browsers.
            hydrateVideoSources(videoEl);

            function onCanPlayThrough() {
                const duration = videoEl.duration;
                const frameCount = Math.min(120, Math.max(2, Math.ceil(duration * 30)));
                videoEl.pause();

                let i = 0;
                const divisor = frameCount - 1;

                const rangeDuration = (END_FRACTION - START_FRACTION) * duration;
                const startTime = START_FRACTION * duration;

                function captureNext() {
                    if (i >= frameCount) {
                        return;
                    }
                    const time = startTime + (i / divisor) * rangeDuration;
                    videoEl.currentTime = time;
                    videoEl.addEventListener('seeked', function onSeeked() {
                        videoEl.removeEventListener('seeked', onSeeked);
                        createImageBitmap(videoEl).then(function(bitmap) {
                            frames.push(bitmap);
                            if (frames.length === 1) {
                                drawFrame(0);
                                // First frame is ready: fade in the helix canvas.
                                if ('classList' in section) {
                                    section.classList.add('hero--helix-ready');
                                }
                            }
                            i++;
                            captureNext();
                        });
                    }, {once: true});
                }

                captureNext();
            }

            if (videoEl.readyState >= 2) {
                onCanPlayThrough();
            } else {
                videoEl.addEventListener('canplaythrough', onCanPlayThrough, {once: true});
            }
        }

        function handleScroll() {
            if (!section) return;
            const rect = section.getBoundingClientRect();
            // 0 when the section top is aligned with the top of the viewport
            // (page load), 1 once it has scrolled ~its full height past the top.
            const scrolled = -rect.top / (rect.height * PLAY_FRACTION);
            targetProgress = Math.max(0, Math.min(1, scrolled));
        }

        const scrollHandler = function() {
            if (timeout) {
                window.cancelAnimationFrame(timeout);
            }

            timeout = window.requestAnimationFrame(function() {
                handleScroll();
                drawFrame(targetProgress);
            });
        };

        extractFrames();
        window.addEventListener('scroll', scrollHandler);
    }

    function autoInit() {
        const sections = document.querySelectorAll('[data-helix-section]');
        for (let i = 0; i < sections.length; i++) {
            const section = sections[i];
            initHelixCanvas(section);
        }
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', autoInit);
    } else {
        autoInit();
    }
})();

